import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  MessageCircle, Plus, Loader2, Trash2, Edit2, Instagram, Zap,
  CheckCircle2, XCircle, Info, Send, Radio, FlaskConical,
} from 'lucide-react';
import { toast } from 'sonner';

const EMPTY = {
  id: null, name: '', connection_id: '', ig_account_id: '', ig_username: '',
  scope: 'all', media_id: '', keywords: '', match: 'contains',
  public_reply_enabled: true, public_reply_text: 'Check your DMs! 📩',
  dm_enabled: true, dm_text: "Thanks for commenting! Here's the link you asked for:",
  dm_url: '', add_to_email_list: false, email_list_id: '', lead_tag: 'instagram-comment', active: true,
};

const statusBadge = (status) => {
  const map = {
    sent: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'sent' },
    failed: { cls: 'bg-red-50 text-red-700 border-red-200', label: 'failed' },
    skipped: { cls: 'bg-zinc-100 text-zinc-500 border-zinc-200', label: 'off' },
    off: { cls: 'bg-zinc-100 text-zinc-500 border-zinc-200', label: 'off' },
    simulated: { cls: 'bg-indigo-50 text-indigo-600 border-indigo-200', label: 'sim' },
    no_match: { cls: 'bg-amber-50 text-amber-700 border-amber-200', label: 'no match' },
    skipped_window: { cls: 'bg-amber-50 text-amber-700 border-amber-200', label: '>7d window' },
  };
  const m = map[status] || map.skipped;
  return <Badge variant="outline" className={`text-xs ${m.cls}`}>{m.label}</Badge>;
};

export const CommentAutomationPage = () => {
  const { api, user } = useAuth();
  const [rules, setRules] = useState([]);
  const [igTargets, setIgTargets] = useState([]);
  const [emailLists, setEmailLists] = useState([]);
  const [activity, setActivity] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [targetsLoading, setTargetsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [media, setMedia] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      // Fast DB-only calls — render the page immediately on these.
      const [r, a, c, l] = await Promise.all([
        api.get('/automation/ig-rules'),
        api.get('/automation/ig-activity').catch(() => ({ data: { activity: [] } })),
        api.get('/automation/ig-config').catch(() => ({ data: null })),
        api.get('/email/lists').catch(() => ({ data: { lists: [] } })),
      ]);
      setRules(r.data?.rules || []);
      setActivity(a.data?.activity || []);
      setConfig(c.data);
      setEmailLists(l.data?.lists || (Array.isArray(l.data) ? l.data : []));
    } finally { setLoading(false); }
    // Instagram targets hit the live Graph API (slow) and are only needed
    // inside the dialog — load them in the background so the page is instant.
    setTargetsLoading(true);
    api.get('/connections/targets')
      .then((t) => setIgTargets((t.data?.targets || []).filter((x) => x.platform === 'instagram')))
      .catch(() => setIgTargets([]))
      .finally(() => setTargetsLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  // Load recent posts when the user targets a specific post for a chosen account.
  useEffect(() => {
    if (!dialogOpen || form.scope !== 'media' || !form.connection_id || !form.ig_account_id) return;
    let cancelled = false;
    setMediaLoading(true);
    api.get('/automation/ig-media', { params: { connection_id: form.connection_id, ig_account_id: form.ig_account_id } })
      .then((res) => { if (!cancelled) setMedia(res.data?.media || []); })
      .catch(() => { if (!cancelled) setMedia([]); })
      .finally(() => { if (!cancelled) setMediaLoading(false); });
    return () => { cancelled = true; };
  }, [dialogOpen, form.scope, form.connection_id, form.ig_account_id]); // eslint-disable-line

  const openNew = () => { setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (rule) => {
    setForm({ ...rule, keywords: (rule.keywords || []).join(', ') });
    setDialogOpen(true);
  };

  const onPickAccount = (igAccountId) => {
    const tgt = igTargets.find((x) => x.account_id === igAccountId);
    if (!tgt) return;
    setForm((f) => ({
      ...f,
      ig_account_id: tgt.account_id,
      connection_id: tgt.connection_id,
      ig_username: (tgt.account_name || '').replace(/^@/, ''),
    }));
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Give the rule a name');
    if (!form.ig_account_id) return toast.error('Pick an Instagram account');
    const keywords = form.keywords.split(',').map((k) => k.trim()).filter(Boolean);
    if (!keywords.length) return toast.error('Add at least one trigger keyword');
    if (!form.public_reply_enabled && !form.dm_enabled) return toast.error('Enable a public reply and/or a DM');
    if (form.add_to_email_list && !form.email_list_id) return toast.error('Pick an email list (or turn off "Add to email list")');
    const body = { ...form, keywords };
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/automation/ig-rules/${form.id}`, body);
        toast.success('Rule updated');
      } else {
        await api.post('/automation/ig-rules', body);
        toast.success('Rule created');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Could not save rule'));
    } finally { setSaving(false); }
  };

  const toggleActive = async (rule) => {
    try {
      await api.put(`/automation/ig-rules/${rule.id}`, { active: !rule.active });
      setRules((rs) => rs.map((r) => (r.id === rule.id ? { ...r, active: !r.active } : r)));
    } catch { toast.error('Could not update'); }
  };

  const remove = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"?`)) return;
    try { await api.delete(`/automation/ig-rules/${rule.id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Could not delete'); }
  };

  const subscribe = async (rule) => {
    try {
      await api.post(`/automation/ig-rules/${rule.id}/subscribe`);
      setRules((rs) => rs.map((r) => (r.id === rule.id ? { ...r, webhook_subscribed: true } : r)));
      toast.success('Webhook enabled — this account is now subscribed to comment events');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Subscription failed');
    }
  };

  const [testRule, setTestRule] = useState(null);
  const [testText, setTestText] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const openTest = (rule) => {
    setTestRule(rule);
    setTestText((rule.keywords || [])[0] ? `I want the ${(rule.keywords || [])[0]}!` : 'GUIDE');
    setTestResult(null);
  };

  const runTest = async () => {
    if (!testRule) return;
    setTesting(true);
    try {
      const r = await api.post(`/automation/ig-rules/${testRule.id}/test`, {
        comment_text: testText, from_username: 'test_user',
      });
      setTestResult(r.data);
      load(); // refresh Activity with the new test entry
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test failed');
    } finally { setTesting(false); }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="comment-automation-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <MessageCircle className="w-7 h-7 text-pink-600" /> Instagram Comment Automation
          </h1>
          <p className="text-zinc-500 mt-1 text-sm">
            When someone comments a keyword on your post, auto-reply publicly and DM them a link — your comment-to-DM funnel.
          </p>
        </div>
        <Button onClick={openNew} data-testid="new-rule-btn" className="bg-pink-600 hover:bg-pink-700">
          <Plus className="w-4 h-4 mr-2" /> New rule
        </Button>
      </div>

      {/* Setup note — admin/app-owner only (end users don't configure webhooks) */}
      {user?.is_admin && config && !config.verify_token_set && (
        <Card className="border-amber-200 bg-amber-50/60">
          <CardContent className="p-4 flex gap-3 text-sm text-amber-800">
            <Info className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              Webhook verify token isn't configured on the server yet. Rules will save, but comment events
              won't be delivered until setup is complete in the Meta App Dashboard.
            </div>
          </CardContent>
        </Card>
      )}
      {user?.is_admin && config?.callback_url && (
        <Card className="border-indigo-200 bg-indigo-50/40">
          <CardContent className="p-4 text-sm space-y-2">
            <div className="font-semibold flex items-center gap-2 text-zinc-800">
              <Radio className="w-4 h-4 text-indigo-600" /> Webhook setup — do this once
            </div>
            <div className="text-zinc-600">
              <span className="font-medium">Step 1 (Meta App Dashboard):</span> open <b>Instagram → API setup with Instagram login → Webhooks</b> (or App Dashboard → Webhooks → Instagram). Paste the callback URL + verify token below, click <b>Verify and save</b>, then subscribe to the <code>comments</code> field.
            </div>
            <div className="text-zinc-500 pl-1">Callback URL: <code className="text-zinc-800 break-all" data-testid="webhook-callback-url">{config.callback_url}</code></div>
            <div className="text-zinc-500 pl-1">Verify token: {config.verify_token_set ? <code className="text-zinc-800">configured on server ✓</code> : <span className="text-amber-600">not set on server</span>}</div>
            <div className="text-zinc-600">
              <span className="font-medium">Step 2 (here):</span> click the <b>Enable webhook</b> button on each rule below to subscribe that Instagram account to comment events.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rules */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-pink-600" /></div>
      ) : rules.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-10 text-center text-zinc-500">
            <Zap className="w-10 h-10 mx-auto mb-3 text-zinc-300" />
            <p className="font-medium text-zinc-700">No automation rules yet</p>
            <p className="text-sm mt-1">Create your first keyword → DM rule to start capturing leads from comments.</p>
            <Button onClick={openNew} className="mt-4 bg-pink-600 hover:bg-pink-700"><Plus className="w-4 h-4 mr-2" />New rule</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="rules-list">
          {rules.map((rule) => (
            <Card key={rule.id} data-testid={`rule-card-${rule.id}`} className={rule.active ? '' : 'opacity-60'}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{rule.name}</span>
                      <Badge variant="outline" className="text-xs"><Instagram className="w-3 h-3 mr-1" />@{rule.ig_username || 'account'}</Badge>
                      {rule.scope === 'media' && <Badge variant="outline" className="text-xs">single post</Badge>}
                      <span className="text-xs text-zinc-400">{rule.trigger_count || 0} triggers</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {(rule.keywords || []).map((k) => (
                        <span key={k} className="text-xs px-2 py-0.5 rounded-full bg-pink-50 text-pink-700 border border-pink-100">{k}</span>
                      ))}
                    </div>
                    <div className="mt-2 text-xs text-zinc-500 space-y-0.5">
                      {rule.public_reply_enabled && <div>💬 Public reply: “{rule.public_reply_text}”</div>}
                      {rule.dm_enabled && <div>📩 DM: “{rule.dm_text}”{rule.dm_url ? ` → ${rule.dm_url}` : ''}</div>}
                      {rule.add_to_email_list && (
                        <div className="text-emerald-600">📧 Adds leads to: {(emailLists.find((l) => l.id === rule.email_list_id)?.name) || 'email list'}</div>
                      )}
                    </div>
                    {!rule.webhook_subscribed && (
                      <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5" data-testid={`webhook-warning-${rule.id}`}>
                        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        <span>Comments won't trigger yet — click <b>Enable webhook</b> to subscribe this account to comment events.</span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <Button
                      size="sm"
                      onClick={() => subscribe(rule)}
                      data-testid={`subscribe-${rule.id}`}
                      variant={rule.webhook_subscribed ? 'outline' : 'default'}
                      className={rule.webhook_subscribed
                        ? 'border-emerald-200 text-emerald-700 hover:bg-emerald-50 h-8'
                        : 'bg-amber-500 hover:bg-amber-600 text-white h-8 animate-pulse'}
                      title={rule.webhook_subscribed ? 'Webhook active — click to re-subscribe' : 'Subscribe this account to comment events'}
                    >
                      {rule.webhook_subscribed
                        ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Webhook on</>
                        : <><Radio className="w-3.5 h-3.5 mr-1.5" /> Enable webhook</>}
                    </Button>
                    <div className="flex items-center gap-1">
                      <Switch checked={!!rule.active} onCheckedChange={() => toggleActive(rule)} data-testid={`toggle-${rule.id}`} />
                      <Button variant="ghost" size="icon" onClick={() => openTest(rule)} title="Test this rule" data-testid={`test-${rule.id}`}>
                        <FlaskConical className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(rule)} data-testid={`edit-${rule.id}`}><Edit2 className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => remove(rule)} className="text-red-500" data-testid={`delete-${rule.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Activity */}
      {activity.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-2 flex items-center gap-2"><Send className="w-4 h-4 text-indigo-600" /> Recent activity</h2>
          <Card>
            <CardContent className="p-0 divide-y">
              {activity.slice(0, 30).map((ev) => (
                <div key={ev.id} className="p-3 flex items-center justify-between gap-3 text-sm" data-testid="activity-row">
                  <div className="min-w-0">
                    <div className="truncate">
                      {ev.test && <Badge variant="outline" className="text-[10px] mr-1 bg-indigo-50 text-indigo-600 border-indigo-200">test</Badge>}
                      <b>@{ev.from_username || 'someone'}</b>: <span className="text-zinc-500">{ev.comment_text}</span>
                    </div>
                    <div className="text-xs text-zinc-400">{ev.rule_name} · {new Date(ev.created_at).toLocaleString()}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-zinc-400">reply</span>{statusBadge(ev.public_reply_status)}
                    <span className="text-xs text-zinc-400">dm</span>{statusBadge(ev.dm_status)}
                    {ev.lead_status && ev.lead_status !== 'skipped' && (
                      <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                        {ev.lead_status === 'captured' ? '+lead' : ev.lead_status === 'exists' ? 'lead✓' : 'lead!'}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Test rule dialog */}
      <Dialog open={!!testRule} onOpenChange={(o) => { if (!o) setTestRule(null); }}>
        <DialogContent className="max-w-md" data-testid="test-rule-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FlaskConical className="w-5 h-5 text-indigo-600" /> Test rule</DialogTitle>
            <DialogDescription>
              Dry-run “{testRule?.name}” against a sample comment. Validates keyword matching and your Instagram connection, and previews the reply + DM. It does <b>not</b> post to Instagram.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Sample comment text</Label>
              <Input value={testText} onChange={(e) => setTestText(e.target.value)} placeholder="Type a comment to test…" data-testid="test-comment-input" />
              <p className="text-xs text-zinc-400 mt-1">Keywords: {(testRule?.keywords || []).join(', ') || '—'}</p>
            </div>
            {testResult && (
              <div className="rounded-lg border p-3 space-y-2 text-sm" data-testid="test-result">
                <div className="flex items-center gap-2">
                  {testResult.matched ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-amber-600" />}
                  <span>{testResult.matched ? 'Keyword matched' : 'No keyword match — rule would not trigger'}</span>
                </div>
                <div className="flex items-center gap-2">
                  {testResult.account_reachable ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-red-600" />}
                  <span>{testResult.connection_message}</span>
                </div>
                {testResult.public_reply_preview && (
                  <div className="text-zinc-600"><b>Public reply:</b> “{testResult.public_reply_preview}”</div>
                )}
                {testResult.dm_preview && (
                  <div className="text-zinc-600 whitespace-pre-line"><b>DM:</b> {testResult.dm_preview}</div>
                )}
                {testResult.would_capture_lead && <div className="text-emerald-600">📧 Would add the commenter as a lead.</div>}
                <p className="text-xs text-zinc-400 pt-1">Logged to Recent activity as a test entry.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestRule(null)}>Close</Button>
            <Button onClick={runTest} disabled={testing} className="bg-indigo-600 hover:bg-indigo-700" data-testid="run-test-btn">
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Run test'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rule dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="rule-dialog">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Edit rule' : 'New comment-to-DM rule'}</DialogTitle>
            <DialogDescription>Trigger on a keyword, reply publicly, and DM the link.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Rule name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Lead magnet — GUIDE" data-testid="rule-name-input" />
            </div>
            <div>
              <Label>Instagram account</Label>
              <Select value={form.ig_account_id} onValueChange={onPickAccount}>
                <SelectTrigger data-testid="ig-account-select"><SelectValue placeholder={targetsLoading ? 'Loading accounts…' : (igTargets.length ? 'Select account' : 'No Instagram connected')} /></SelectTrigger>
                <SelectContent>
                  {igTargets.map((t) => (
                    <SelectItem key={t.account_id} value={t.account_id}>{t.account_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {igTargets.length === 0 && <p className="text-xs text-amber-600 mt-1">Connect an Instagram Business account on Connections first.</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Applies to</Label>
                <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                  <SelectTrigger data-testid="scope-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All posts</SelectItem>
                    <SelectItem value="media">A specific post</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Match</Label>
                <Select value={form.match} onValueChange={(v) => setForm({ ...form, match: v })}>
                  <SelectTrigger data-testid="match-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contains">Contains keyword</SelectItem>
                    <SelectItem value="exact">Exact match</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.scope === 'media' && (
              <div>
                <Label>Pick the post</Label>
                {mediaLoading ? (
                  <div className="flex items-center gap-2 text-sm text-zinc-500 py-4">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading your recent posts…
                  </div>
                ) : media.length > 0 ? (
                  <>
                    <div className="grid grid-cols-4 gap-2 max-h-56 overflow-y-auto p-1" data-testid="media-picker">
                      {media.map((m) => (
                        <button
                          type="button"
                          key={m.id}
                          onClick={() => setForm({ ...form, media_id: m.id })}
                          title={m.caption || m.id}
                          data-testid={`media-tile-${m.id}`}
                          className={`relative aspect-square rounded-md overflow-hidden border-2 transition-colors ${form.media_id === m.id ? 'border-pink-600 ring-2 ring-pink-200' : 'border-transparent hover:border-zinc-300'}`}
                        >
                          {m.thumbnail ? (
                            <img src={m.thumbnail} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-zinc-100 flex items-center justify-center"><Instagram className="w-5 h-5 text-zinc-400" /></div>
                          )}
                          {form.media_id === m.id && (
                            <span className="absolute top-1 right-1 bg-pink-600 text-white rounded-full p-0.5"><CheckCircle2 className="w-3.5 h-3.5" /></span>
                          )}
                          {m.media_type === 'VIDEO' && (
                            <span className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] px-1 rounded">Reel</span>
                          )}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-zinc-400 mt-1">
                      {form.media_id ? <>Selected post: <code className="text-zinc-600">{form.media_id}</code></> : 'Tap a post above to target it.'}
                    </p>
                  </>
                ) : (
                  <p className="text-xs text-amber-600 py-2">
                    Couldn't load recent posts{form.ig_account_id ? '' : ' — pick an Instagram account first'}. Paste the post ID below instead.
                  </p>
                )}
                <Input
                  value={form.media_id}
                  onChange={(e) => setForm({ ...form, media_id: e.target.value })}
                  placeholder="…or paste a post (media) ID"
                  data-testid="media-id-input"
                  className="mt-2"
                />
              </div>
            )}
            <div>
              <Label>Trigger keywords (comma-separated)</Label>
              <Input value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} placeholder="GUIDE, LINK, INFO" data-testid="keywords-input" />
            </div>

            <div className="rounded-lg border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2"><MessageCircle className="w-4 h-4" /> Public reply under the comment</Label>
                <Switch checked={form.public_reply_enabled} onCheckedChange={(v) => setForm({ ...form, public_reply_enabled: v })} data-testid="public-toggle" />
              </div>
              {form.public_reply_enabled && (
                <Input value={form.public_reply_text} onChange={(e) => setForm({ ...form, public_reply_text: e.target.value })} placeholder="Check your DMs! 📩" data-testid="public-text-input" />
              )}
            </div>

            <div className="rounded-lg border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2"><Send className="w-4 h-4" /> Private DM to the commenter</Label>
                <Switch checked={form.dm_enabled} onCheckedChange={(v) => setForm({ ...form, dm_enabled: v })} data-testid="dm-toggle" />
              </div>
              {form.dm_enabled && (
                <>
                  <Textarea value={form.dm_text} onChange={(e) => setForm({ ...form, dm_text: e.target.value })} rows={2} placeholder="Thanks! Here's your link:" data-testid="dm-text-input" />
                  <Input value={form.dm_url} onChange={(e) => setForm({ ...form, dm_url: e.target.value })} placeholder="https://yoursite.com/guide" data-testid="dm-url-input" />
                  <p className="text-xs text-zinc-400">The link is appended to the DM. Instagram allows one DM per comment, within 7 days.</p>
                </>
              )}
            </div>

            <div className="rounded-lg border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2"><Send className="w-4 h-4 rotate-45" /> Add commenter to email list</Label>
                <Switch checked={form.add_to_email_list} onCheckedChange={(v) => setForm({ ...form, add_to_email_list: v })} data-testid="email-toggle" />
              </div>
              {form.add_to_email_list && (
                <>
                  <Select value={form.email_list_id} onValueChange={(v) => setForm({ ...form, email_list_id: v })}>
                    <SelectTrigger data-testid="email-list-select"><SelectValue placeholder={emailLists.length ? 'Select list' : 'No email lists yet'} /></SelectTrigger>
                    <SelectContent>
                      {emailLists.map((l) => (<SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <Input value={form.lead_tag} onChange={(e) => setForm({ ...form, lead_tag: e.target.value })} placeholder="Tag (e.g. instagram-comment)" data-testid="lead-tag-input" />
                  <p className="text-xs text-zinc-400">
                    Instagram doesn't share the commenter's email, so they're added as a <b>lead</b> (by IG handle) — excluded from sends until they submit their email via your DM link.
                  </p>
                  {emailLists.length === 0 && <p className="text-xs text-amber-600">Create a list under Email Marketing first.</p>}
                </>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-pink-600 hover:bg-pink-700" data-testid="save-rule-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (form.id ? 'Save changes' : 'Create rule')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommentAutomationPage;
